/**
 * MusicDirector — pure crossfade state machine for MANOR's dynamic stage music.
 *
 * No Phaser, no Howler import — fully testable in Node/Vitest.
 * MusicDirectorAdapter (game-side) calls tick() each frame and applies the
 * returned PlayState to actual HowlLike instances.
 *
 * Crossfade model: equal-power (cos/sin) over ~3 s by default.
 * Graceful fallback: resolveMovement clamps to last available movement so
 * missing Movement II/III never silences the audio or crashes.
 */

import {
  MUSIC_MANIFEST,
  resolveBossTrack,
  resolveMovement,
  type MusicManifest,
  type MusicKey,
  type UIScene,
} from '@/data/musicManifest';

/** ~4 bars at ~80 bpm ≈ 3 s crossfade. */
export const DEFAULT_CROSSFADE_MS = 3000;

/** What the adapter should be playing each tick. */
export interface PlayState {
  /** Fading-out track; null when not crossfading or when crossfade just finished. */
  outgoing: { key: MusicKey; volume: number } | null;
  /** Active / fading-in track; null only when fully idle. */
  incoming: { key: MusicKey; volume: number } | null;
  /** True on the single tick that completes a crossfade — adapter should stop outgoing. */
  crossfadeDone: boolean;
}

// ── Internal context ──────────────────────────────────────────────────────────

type PlayContext =
  | { type: 'idle' }
  | { type: 'ui'; scene: UIScene; trackKey: MusicKey }
  | { type: 'stage'; stageId: number; movementIndex: number; trackKey: MusicKey }
  | { type: 'boss'; trackKey: MusicKey; originStageId: number; originMovement: number }
  | { type: 'victory'; trackKey: MusicKey };

interface ActiveCrossfade {
  outgoingKey: MusicKey;
  incomingKey: MusicKey;
  elapsedMs: number;
  readonly durationMs: number;
  readonly pendingContext: PlayContext;
}

// ── MusicDirector ─────────────────────────────────────────────────────────────

export class MusicDirector {
  private readonly manifest: MusicManifest;
  private readonly crossfadeDurationMs: number;

  private context: PlayContext = { type: 'idle' };
  private crossfade: ActiveCrossfade | null = null;

  constructor(
    manifest: MusicManifest = MUSIC_MANIFEST,
    crossfadeDurationMs: number = DEFAULT_CROSSFADE_MS,
  ) {
    this.manifest = manifest;
    this.crossfadeDurationMs = crossfadeDurationMs;
  }

  // ── Queries ─────────────────────────────────────────────────────────────────

  /**
   * The track key that is currently primary (incoming during crossfade, or
   * the steady-state key). Null when fully idle.
   */
  get currentKey(): MusicKey | null {
    if (this.crossfade !== null) return this.crossfade.incomingKey;
    if (this.context.type === 'idle') return null;
    return (this.context as Exclude<PlayContext, { type: 'idle' }>).trackKey;
  }

  /** Current stage movement index (0-based), or -1 when not in a stage context. */
  get movementIndex(): number {
    if (this.context.type === 'stage') return this.context.movementIndex;
    return -1;
  }

  get isCrossfading(): boolean {
    return this.crossfade !== null;
  }

  // ── Events ──────────────────────────────────────────────────────────────────

  /** Start stage music from Movement I. No crossfade when coming from idle. */
  handleStageStart(stageId: number): void {
    const trackKey = resolveMovement(this.manifest, stageId, 0);
    if (trackKey === null) {
      console.warn(`[MusicDirector] No manifest entry for stage ${stageId} — staying silent.`);
      this.context = { type: 'idle' };
      this.crossfade = null;
      return;
    }
    this._transitionTo({ type: 'stage', stageId, movementIndex: 0, trackKey }, trackKey);
  }

  /**
   * Advance to the next movement on checkpoint.
   * When already on the last movement, holds it — no crossfade, no silence.
   * No-ops if not currently in a stage context.
   */
  handleCheckpoint(): void {
    if (this.context.type !== 'stage') return;
    const { stageId, movementIndex } = this.context;
    const nextIndex = movementIndex + 1;
    const nextKey = resolveMovement(this.manifest, stageId, nextIndex);

    // resolveMovement clamps, so nextKey === currentMovementKey when at the last movement.
    if (nextKey === null || nextKey === this.context.trackKey) return;

    this._transitionTo(
      { type: 'stage', stageId, movementIndex: nextIndex, trackKey: nextKey },
      nextKey,
    );
  }

  /** Crossfade from the current movement to the boss track. */
  handleBossTriggered(bossId: string): void {
    const bossKey = resolveBossTrack(this.manifest, bossId);
    const [originStageId, originMovement] = this._stagePosition();
    this._transitionTo({ type: 'boss', trackKey: bossKey, originStageId, originMovement }, bossKey);
  }

  /** Crossfade to the victory sting on boss defeat. */
  handleBossDefeated(): void {
    const trackKey = this.manifest.uiTracks.victory;
    this._transitionTo({ type: 'victory', trackKey }, trackKey);
  }

  /** Crossfade to the appropriate UI track on scene change. */
  handleSceneChange(scene: UIScene): void {
    const trackKey = this.manifest.uiTracks[scene];
    this._transitionTo({ type: 'ui', scene, trackKey }, trackKey);
  }

  // ── Tick ────────────────────────────────────────────────────────────────────

  /**
   * Advance the crossfade by deltaMs and return the current PlayState.
   * Call once per frame from the adapter's update().
   */
  tick(deltaMs: number): PlayState {
    if (this.crossfade === null) {
      const key = this.currentKey;
      return {
        outgoing: null,
        incoming: key !== null ? { key, volume: 1 } : null,
        crossfadeDone: false,
      };
    }

    this.crossfade.elapsedMs += deltaMs;
    const t = Math.min(this.crossfade.elapsedMs / this.crossfade.durationMs, 1);

    // Equal-power crossfade.
    const outVol = Math.cos(t * (Math.PI / 2));
    const inVol = Math.sin(t * (Math.PI / 2));

    const done = t >= 1;
    const { outgoingKey, incomingKey, pendingContext } = this.crossfade;

    if (done) {
      this.context = pendingContext;
      this.crossfade = null;
    }

    return {
      outgoing: done ? null : { key: outgoingKey, volume: outVol },
      incoming: { key: incomingKey, volume: done ? 1 : inVol },
      crossfadeDone: done,
    };
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private _stagePosition(): [stageId: number, movementIndex: number] {
    if (this.context.type === 'stage') return [this.context.stageId, this.context.movementIndex];
    if (this.context.type === 'boss')
      return [this.context.originStageId, this.context.originMovement];
    return [1, 0];
  }

  private _transitionTo(pending: PlayContext, incomingKey: MusicKey): void {
    const outgoingKey = this.currentKey;

    if (outgoingKey === null) {
      // Nothing playing — start immediately without a crossfade.
      this.context = pending;
      this.crossfade = null;
      return;
    }

    if (outgoingKey === incomingKey) {
      // Same track — update context without a crossfade.
      this.context = pending;
      this.crossfade = null;
      return;
    }

    this.crossfade = {
      outgoingKey,
      incomingKey,
      elapsedMs: 0,
      durationMs: this.crossfadeDurationMs,
      pendingContext: pending,
    };
  }
}
